/**
 * ONE BOX THAT FINDS THE THING.
 *
 * The complaint this screen answers, in the owner's words: "the user has to
 * navigate through multiple screens to check a particular challan or
 * transaction." He was right. Every register had its own search and none of
 * them knew about the others, so finding challan DC/26-27/00006 meant knowing
 * it was a dispatch, going to Dispatch, clearing the status filter, and
 * reading a list — four moves to answer a question he could already state
 * exactly.
 *
 * So: type anything a person actually knows — a cylinder serial, a challan
 * number, a customer's name, an order number — and this asks every register at
 * once and shows what came back, grouped, with the strongest answer first.
 *
 * When the query can only mean one thing — a serial that matches one cylinder,
 * a challan number that matches one challan — it GOES there rather than showing
 * a list of one. A search result page with a single row on it is a screen
 * nobody wanted to look at.
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useRun, go, routeQuery } from '../hooks.js';
import { Banner, Button, Card, Loading, Pill, Register, Serial } from '../ui.js';
import { Page } from '../shell.js';
import { count, date, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('find')!.messages;

interface Hit { kind: 'cylinder' | 'challan' | 'order' | 'customer' | 'paper'; id: number; title: string; detail: string; note: string; href: string; alias?: string }

/** What the query looks like, so an exact answer can be taken straight there. */
const looksLikeSerial  = (q: string) => /^(CYL-)?[A-Za-z0-9][A-Za-z0-9/-]{2,}$/.test(q) && /\d/.test(q);
const looksLikeChallan = (q: string) => /\//.test(q);

export function Find({ session }: { session: Session }) {
  const initial = routeQuery('q') ?? '';
  const [q, setQ] = useState(initial);
  const [typed, setTyped] = useState(initial);
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [totals, setTotals] = useState<Partial<Record<Hit['kind'], number>>>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const runner = useRun(MESSAGES);

  useEffect(() => { if (initial) void search(initial); /* eslint-disable-next-line */ }, []);

  async function search(term: string) {
    const t = term.trim();
    if (!t) { setHits(null); return; }
    setBusy(true); setFailed(null); runner.clear();
    const found: Hit[] = [];
    // Asked in parallel and each failure swallowed on its own: a person without
    // permission to read orders should still find his cylinder.
    const [cyl, chal, ord, cust, papers] = await Promise.all([
      can(session, 'view.cylinders') ? api.view('cylinders.search', { q: t, pageSize: 12 }).catch(() => null) : null,
      can(session, 'view.dispatch') ? api.view('challans.list', { q: t, pageSize: 12 }).catch(() => null) : null,
      can(session, 'view.orders') ? api.view('orders.list', { q: t, pageSize: 12 }).catch(() => null) : null,
      can(session, 'view.customers') ? api.view('customers.list', { q: t, pageSize: 12 }).catch(() => null) : null,
      // 10 Oct — a paper number finds its paper
      can(session, 'document.read') ? api.view('documents.list', { q: t, pageSize: 12 }).catch(() => null) : null,
    ]);

    for (const r of papers?.rows ?? []) found.push({
      kind: 'paper', id: r.documentId, href: api.documentUrl(r.documentId),
      title: `${words(r.kind)} ${r.number ?? `#${r.documentId}`}`,
      detail: r.customerName ?? '', note: r.challanNumber ? `challan ${r.challanNumber}` : 'open the PDF',
    });

    for (const r of chal?.rows ?? []) found.push({
      kind: 'challan', id: r.challanId, href: `#/${r.type === 'return' ? 'returns' : 'dispatch'}/${r.challanId}`,
      // an imported challan may carry only its OLD number; either identity is the title
      title: r.number ?? r.legacyNumber ?? `Challan #${r.challanId}`,
      ...(r.legacyNumber ? { alias: r.legacyNumber } : {}),
      detail: `${r.customerName} · ${r.siteName}`,
      note: `${words(r.type)} · ${date(r.eventDate)} · ${r.status}${r.journey ? ` · ${words(r.journey)}` : ''}`,
    });
    for (const r of cyl?.rows ?? []) found.push({
      kind: 'cylinder', id: r.cylinderId, href: `#/cylinders/${r.cylinderId}`,
      title: r.internalId, detail: `${r.serialNo} · ${words(r.gasService)}`,
      note: `${words(r.state)} · held by ${words(r.holder)}`,
    });
    for (const r of ord?.rows ?? []) found.push({
      kind: 'order', id: r.orderId, href: `#/orders/${r.orderId}`,
      title: `Order #${r.orderId}`, detail: `${r.customerName} · ${r.siteName}`,
      note: `${r.status} · ${count(r.qtyOutstanding)} outstanding · wanted ${date(r.requestedDate)}`,
    });
    for (const r of cust?.rows ?? []) found.push({
      kind: 'customer', id: r.customerId, href: `#/customers/${r.customerId}`,
      title: r.name, detail: r.gstin ?? 'no GSTIN on file',
      note: `${count(r.activeSites)} site(s)${r.active ? '' : ' · closed'}`,
    });

    setBusy(false);
    setTotals({
      ...(chal ? { challan: chal.page.total } : {}), ...(cyl ? { cylinder: cyl.page.total } : {}),
      ...(ord ? { order: ord.page.total } : {}), ...(cust ? { customer: cust.page.total } : {}),
    });
    if (found.length === 0) { setHits([]); setFailed(t); return; }

    // An answer that can only mean one thing is taken, not listed.
    const exact = found.filter((h) =>
      h.title.toLowerCase() === t.toLowerCase() ||
      (h.alias ?? '').toLowerCase() === t.toLowerCase() ||
      (h.kind === 'cylinder' && h.detail.toLowerCase().startsWith(t.toLowerCase() + ' ')));
    if (exact.length === 1 && (looksLikeSerial(t) || looksLikeChallan(t))) { go(exact[0]!.href); return; }
    setHits(found);
  }

  const groups: { kind: Hit['kind']; label: string; rows: Hit[] }[] = ([
    { kind: 'challan',  label: 'Challans and returns', rows: (hits ?? []).filter((h) => h.kind === 'challan') },
    { kind: 'cylinder', label: 'Cylinders',            rows: (hits ?? []).filter((h) => h.kind === 'cylinder') },
    { kind: 'order',    label: 'Orders',               rows: (hits ?? []).filter((h) => h.kind === 'order') },
    { kind: 'customer', label: 'Customers',            rows: (hits ?? []).filter((h) => h.kind === 'customer') },
    { kind: 'paper',    label: 'Papers',               rows: (hits ?? []).filter((h) => h.kind === 'paper') },
  ] as { kind: Hit['kind']; label: string; rows: Hit[] }[]).filter((g) => g.rows.length > 0);

  return (
    <Page title="Find" sub="A serial, a challan number, a customer, an order number — whatever you already know.">
      <Card>
        <form onSubmit={(e: { preventDefault: () => void }) => { e.preventDefault(); setQ(typed); void search(typed); }}>
          <div className="toolbar">
            <div className="field grow">
              <label htmlFor="find-q">What are you looking for?</label>
              <input id="find-q" type="search" value={typed} autoFocus autoComplete="off"
                     placeholder="KIGL-1 · DC/26-27/00006 · Demo Customer · 7"
                     onChange={(e: { target: { value: string } }) => setTyped(e.target.value)} />
            </div>
            <Button type="submit" kind="primary" disabled={busy || !typed.trim()}>{busy ? 'Looking…' : 'Find it'}</Button>
          </div>
        </form>
      </Card>

      {busy ? <Card><Loading what="everything that matches" /></Card> : null}

      {!busy && failed ? (
        <Banner kind="warn" title={`Nothing answers to “${failed}”.`}
                detail="Serials, challan numbers, customer names and order numbers are all searched. A cylinder that has left this company, or a challan in the other company, will not be found — and should not be." />
      ) : null}

      {!busy && hits && hits.length > 0 ? (
        <>
          <p className="muted">{count(hits.length)} match(es) for “{q}”.</p>
          {groups.map((g) => {
            const total = totals[g.kind] ?? g.rows.length;
            const partial = total > g.rows.length;
            return (
            <Card key={g.kind} title={g.label}
                  sub={partial
                    ? `showing the first ${count(g.rows.length)} of ${count(total)} match(es) — narrow the search to see the rest`
                    : `${count(g.rows.length)} match(es)`}>
              <Register
                columns={[
                  { key: 'title', head: 'What', cell: (h: Hit) => h.kind === 'cylinder' ? <Serial>{h.title}</Serial> : <strong>{h.title}</strong> },
                  { key: 'detail', head: 'Whose', cell: (h: Hit) => h.detail },
                  { key: 'note', head: 'Where it stands', cell: (h: Hit) => <span className="muted">{h.note}</span> },
                  { key: 'open', head: '', cell: () => <Pill tone="primary">open</Pill> },
                ]}
                rows={g.rows} empty="none" onOpen={(h) => go(h.href)} />
            </Card>
            );
          })}
        </>
      ) : null}

      {!busy && !hits ? (
        <Card title="What this searches" sub="everything a person is likely to have in front of them">
          <ul className="timeline">
            <li><strong>A cylinder</strong> — its internal id or the serial stamped on it<div className="when">CYL-01001 · KIGL-1</div></li>
            <li><strong>A challan</strong> — dispatch or return, by its number<div className="when">DC/26-27/00006</div></li>
            <li><strong>An order</strong> — by number, or by the customer's own reference<div className="when">7 · PO-4471</div></li>
            <li><strong>A customer</strong> — by name<div className="when">KIGL Demo Customer</div></li>
          </ul>
        </Card>
      ) : null}
    </Page>
  );
}
